/* eslint-disable @typescript-eslint/no-non-null-assertion */
import debounce from 'lodash/debounce';
import { dataLayer } from 'analytics/data-layer';

interface ProductData {
  currencyCode: string;
  element: HTMLElement;
  id: string;
  lineNumber: string;
  list: string;
  name: string;
  offer: string;
  position: number;
  price: number | '';
  productFindingMethod: string | undefined;
  sponsored?: boolean;
  sponsorshipId?: string;
  uom: string;
}

interface LegacyImpression {
  id: string;
  'pod-type': string;
  index: string;
  availability: string;
  sponsored: 'YES' | 'NO';
  sponsored_source: 'citrus_ad' | 'aem' | 'unknown';
}

interface Impression {
  /** Product price currency */
  currencyCode: string;
  /** Product line number */
  id: string;
  /** List name for the product grid, carousel, or other list types. Not to be confused with similar pageType values */
  list: string;
  /** Product name */
  name: string;
  /** Vertical bar " | " separated list of offer and promotion descriptions */
  offer: string;
  /** Position of the product pod in the grid or carousel */
  position: number;
  /** Numeric product current sale unit price */
  price: number | '';
  /** Product ID */
  product_sku: string;
  /** Default UOM for the product */
  uom: string;
  /** Method of finding this product, e.g. from another page's list, relevant for PDPs */
  product_finding_method: string | undefined;
  /** Current list name displaying the product */
  item_list_name: string | undefined;
}

const mapToLegacyImpression = ({
  element,
  sponsored,
  sponsorshipId,
}: ProductData): LegacyImpression => {
  const source = sponsorshipId ? 'citrus_ad' : 'aem';

  return {
    id: element.dataset.productId!,
    'pod-type': element.dataset.productPodType!,
    index: element.dataset.productIndex!,
    availability: element.dataset.productAvailability!,
    sponsored: sponsored ? 'YES' : 'NO',
    sponsored_source: sponsored ? source : 'unknown',
  };
};

const mapToImpression = ({
  currencyCode,
  id,
  lineNumber,
  list,
  name,
  offer,
  position,
  price,
  productFindingMethod,
  uom,
}: ProductData): Impression => {
  return {
    currencyCode,
    id: lineNumber,
    list,
    name,
    offer,
    position,
    price,
    product_sku: id,
    uom,
    product_finding_method: productFindingMethod,
    item_list_name: productFindingMethod,
  };
};

const SIXTY_FPS_MS = 17;
const MAX_ROW_SIZE = 6;
const MAX_DEBOUNCE_MS = SIXTY_FPS_MS * (MAX_ROW_SIZE - 1);

let productDataBuffer: ProductData[] = [];

const reportImpressions = () => {
  const legacyImpressions: LegacyImpression[] = [];
  const impressions: Impression[] = [];

  productDataBuffer.forEach(productData => {
    legacyImpressions.push(mapToLegacyImpression(productData));
    impressions.push(mapToImpression(productData));
  });

  productDataBuffer = [];

  dataLayer.push({
    event: 'product_impression',
    ecommerce: {
      currencyCode: 'GBP',
      product_finding_method: impressions[0].product_finding_method,
      item_list_name: impressions[0].item_list_name,
      items: impressions,
    },
    product_impressions: legacyImpressions,
  });
};

const reportImpressionsDebouncer = debounce(reportImpressions, SIXTY_FPS_MS, {
  maxWait: MAX_DEBOUNCE_MS,
});

export const addProductImpression = (productData: ProductData) => {
  productDataBuffer.push(productData);

  reportImpressionsDebouncer();
};
